import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ExternalLink } from '../../icons/wp-icons';
import { googleAuthApi } from '../../api/googleAuthApi';

// Markenfarben-akzentuiertes Kalender-Icon (Google-Blau). Platzhalter, bis
// die offizielle Google-Calendar-Asset-Datei in /public/brand/ liegt.
const GoogleCalendarIcon = ({ className = 'w-7 h-7' }) => (
  <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg" fill="none">
    <title>Google Calendar</title>
    <rect x="8" y="10" width="32" height="30" rx="3" fill="#fff" stroke="#4285F4" strokeWidth="2.5" />
    <rect x="8" y="10" width="32" height="8" fill="#4285F4" />
    <line x1="16" y1="6" x2="16" y2="14" stroke="#4285F4" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="32" y1="6" x2="32" y2="14" stroke="#4285F4" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="16" cy="26" r="2" fill="#4285F4" />
    <circle cx="24" cy="26" r="2" fill="#4285F4" />
    <circle cx="32" cy="26" r="2" fill="#4285F4" />
    <circle cx="16" cy="33" r="2" fill="#4285F4" />
    <circle cx="24" cy="33" r="2" fill="#4285F4" />
  </svg>
);

function GoogleCalendarSettings({ readOnly = false }) {
  const [connected, setConnected] = useState(false);
  const [accountEmail, setAccountEmail] = useState(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const data = await googleAuthApi.getStatus();
      setConnected(data.connected);
      setAccountEmail(data.email || null);
      setTokenInvalid(!!data.tokenInvalid);
    } catch (error) {
      console.error('Fehler beim Prüfen des Google Calendar Status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);

      // WICHTIG: Popup SOFORT öffnen bevor async-Call!
      // Mobile Browser/PWAs blockieren window.open() wenn es nicht direkt durch User-Aktion kommt
      const width = 550;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
      );

      // Falls Popup blockiert wurde
      if (!popup) {
        alert('Bitte erlauben Sie Popups für diese Seite, um Google Calendar zu verbinden.');
        setConnecting(false);
        return;
      }

      // Lade-Nachricht anzeigen
      popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#f9fafb;"><p>Laden...</p></body></html>');

      // Auth-URL holen und Popup dorthin navigieren
      const { authUrl } = await googleAuthApi.getAuthUrl();
      popup.location.href = authUrl;

      // Popup geschlossen? → Status neu laden
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          setConnecting(false);

          // Status neu laden (falls erfolgreich verbunden)
          setTimeout(() => {
            checkStatus();
          }, 500);
        }
      }, 500);

    } catch (error) {
      console.error('Fehler beim Verbinden mit Google Calendar:', error);
      alert('Fehler beim Verbinden mit Google Calendar');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Google Calendar Verbindung wirklich trennen?')) return;

    try {
      await googleAuthApi.disconnect();
      setConnected(false);
      alert('Google Calendar Verbindung getrennt');
    } catch (error) {
      console.error('Fehler beim Trennen:', error);
      alert('Fehler beim Trennen der Verbindung');
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6 border-t-4" style={{ borderTopColor: '#4285F4' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <GoogleCalendarIcon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-semibold">Google Calendar</h2>
        </div>

        {connected && tokenInvalid ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Verbindung abgelaufen
          </div>
        ) : connected ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Verbunden{accountEmail ? ` (${accountEmail})` : ''}
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Nicht verbunden
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-900">
          <strong>Synchronisation:</strong> Wenn Sie Ihr Google Calendar verbinden,
          werden alle Termine automatisch zwischen CRM und Google Calendar synchronisiert.
        </p>
        <ul className="mt-2 text-sm text-blue-800 space-y-1 ml-4">
          <li>Neuer Termin im CRM - Automatisch in Google Calendar</li>
          <li>Termin im CRM ändern - Update in Google Calendar</li>
          <li>Termin im CRM löschen - Löschen in Google Calendar</li>
          <li>Kunden erhalten automatisch E-Mail-Einladungen</li>
        </ul>
      </div>

      {/* Status & Actions */}
      {connected && tokenInvalid && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-900 font-medium">Die Verbindung zu Google Calendar ist abgelaufen oder wurde widerrufen.</p>
          <p className="text-sm text-red-700 mt-1">
            Termine werden aktuell NICHT mehr synchronisiert. Bitte trennen Sie die Verbindung und verbinden Sie den Kalender neu.
            Bereits gespiegelte Termine werden dabei nicht doppelt angelegt.
          </p>
          {!readOnly && (
            <button
              onClick={async () => { await handleDisconnect(); }}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-button hover:bg-red-700 transition text-sm font-medium"
            >
              Verbindung trennen und neu verbinden
            </button>
          )}
        </div>
      )}

      {connected ? (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-900 font-medium">
              Ihr Google Calendar ist verbunden
            </p>
            <p className="text-sm text-green-700 mt-1">
              Alle Termine werden automatisch synchronisiert.
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleDisconnect}
              className="w-full px-4 py-3 bg-danger text-white rounded-button hover:bg-red-700 transition font-medium"
            >
              Verbindung trennen
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-text font-medium">
              Google Calendar verbinden
            </p>
            <p className="text-sm text-text-muted mt-1">
              {readOnly
                ? 'Sie haben keine Berechtigung, diese Einstellung zu ändern.'
                : 'Klicken Sie auf "Verbinden" um Ihr Google Calendar zu verknüpfen. Sie werden zu Google weitergeleitet um die Verbindung zu autorisieren.'}
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full px-4 py-3 text-white rounded-button hover:opacity-90 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#4285F4' }}
            >
              {connecting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Verbinde...
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  Mit Google Calendar verbinden
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default GoogleCalendarSettings;
